/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.22.0
 */

import type { IPageReportsConstructorTranslation } from '@modules/internationalization/types';
import {
    TableCell,
    TableCellDate,
    TableCellLink,
    TableCellText,
    TableHeader,
    TTableColumnConfig,
} from '@modules/table';
import { useTableReportNameCell } from '@modules/table/components/Cell/Link/useReportName';
import { TableCellTextDouble } from '@modules/table/components/Cell/Text/TextDouble';
import { EFormatData } from '@pages/PageDashboard/types/widgetTypes';

import { ReportId } from '../../types';
import { TableScheduleCellRemove } from './TableScheduleCellRemove';
import { EScheduleColumns, ITableScheduleColumns } from './tableScheduleTypes';

export const TableScheduleColumns = (
    translation: IPageReportsConstructorTranslation): TTableColumnConfig<ITableScheduleColumns> => {

    const { scheduleTable: tSchedule } = translation;

    const useReportName = ({ report, cppoId }: ITableScheduleColumns) => useTableReportNameCell(cppoId, report.def);

    return {
        /* Название */
        report: {
            accessor: EScheduleColumns.REPORT,
            Header: <TableHeader disableSort={true} label={tSchedule.report} name={EScheduleColumns.REPORT} />,
            sticky: 'left',
            disableResizing: true,
            ...TableCellLink<ITableScheduleColumns>(useReportName),
        },
        /* Расписание */
        periodType: {
            accessor: EScheduleColumns.PERIOD_TYPE,
            Header: <TableHeader
                disableSort={true}
                label={tSchedule.periodType}
                name={EScheduleColumns.PERIOD_TYPE}
            />,
            ...TableCellTextDouble<ITableScheduleColumns>(({ periodType, dayOfPeriodName }) => ({
                title: periodType.def,
                subTitle: dayOfPeriodName,
            })),
        },
        /* Время формирования */
        startTime: {
            accessor: EScheduleColumns.START_TIME,
            Header: <TableHeader
                disableSort={true}
                label={tSchedule.startTime}
                name={EScheduleColumns.START_TIME}
            />,
            ...TableCellDate<ITableScheduleColumns>(({ startTime }) => ({
                date: startTime,
                format: EFormatData.OnlyHourMinutes,
            })),
        },
        /* Данные */
        orderAttrType: {
            accessor: EScheduleColumns.ORDER_ATTR_TYPE,
            Header: <TableHeader
                disableSort={true}
                label={tSchedule.orderAttrType}
                name={EScheduleColumns.ORDER_ATTR_TYPE}
            />,
            ...TableCellText<ITableScheduleColumns>(({ orderAttrType }) => orderAttrType.def),
        },
        /* Формат отчета */
        reportFormat: {
            accessor: EScheduleColumns.REPORT_FORMAT,
            Header: <TableHeader
                disableSort={true}
                label={tSchedule.reportFormat}
                name={EScheduleColumns.REPORT_FORMAT}
            />,
            ...TableCellText<ITableScheduleColumns>(({ reportFormat }) => reportFormat.def),
        },
        /* Описание */
        description: {
            accessor: EScheduleColumns.DESCRIPTION,
            Header: <TableHeader
                disableSort={true}
                label={tSchedule.description}
                name={EScheduleColumns.DESCRIPTION}
            />,
            ...TableCellText<ITableScheduleColumns>(({ description }) => description),
        },
        /* Удалить */
        remove: {
            accessor: EScheduleColumns.REMOVE,
            Header: <TableHeader disableSort={true} label={tSchedule.remove} name={EScheduleColumns.REMOVE} />,
            disableResizing: true,
            Cell: props => TableCell(props)({
                render: ({ row }) => <TableScheduleCellRemove cppoId={row.cppoId} reportId={ReportId(row.report.id)} />,
            }),
        },
    };
};
